import * as React from 'react';
import { ArrowLeft, MoreVertical } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { actionIsDisabled, pageHeaderActionClass, splitPageHeaderActions, type PageHeaderAction } from './page-header-actions';

export type { PageHeaderAction } from './page-header-actions';

export type PageHeaderLinkComponent = React.ComponentType<{
  to: string;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}>;

export type PageHeaderProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Allow the subtitle to wrap instead of truncating (default false). Use for
   * subtitles that are a short fact strip rather than free text that could
   * overflow — e.g. "date · time · status". */
  subtitleWrap?: boolean;
  badge?: React.ReactNode;
  /**
   * Back link above the title. `to` + `linkComponent` renders a router link
   * (e.g. so it's a real `<a>` for middle-click/ctrl-click); otherwise (or
   * with no `linkComponent`) it renders as a button calling `onClick`. When
   * both `to` and `onClick` are given with a `linkComponent` present, `onClick`
   * still fires alongside the navigation.
   */
  back?: { label: string; onClick?: () => void; to?: string };
  actions?: PageHeaderAction[];
  /** Extra custom <DropdownMenuItem>s appended to the ⋮ menu. */
  overflow?: React.ReactNode;
  /**
   * Custom action controls the PageHeaderAction shape cannot express (e.g. a
   * DropdownMenu trigger). Rendered in the action row; you own its mobile
   * sizing.
   */
  extra?: React.ReactNode;
  /** Set when `extra` is a single icon-sized control (e.g. a ⋮ menu trigger):
   *  on phones it then stays on the title row instead of its own row. */
  extraCompact?: boolean;
  /**
   * Renders an action that carries a `disabledReason`. This package must not
   * depend on a tooltip implementation, so the host app decides how a reason is
   * shown — the dashboard wraps the disabled button in `AdminOnlyTooltip`.
   * Omitting it is safe: the action is still disabled, just silent.
   */
  renderDisabled?: (node: React.ReactNode, reason: string) => React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  moreLabel?: string;
  linkComponent?: PageHeaderLinkComponent;
  className?: string;
  /** Rendered left of the title block, same row — e.g. a sidebar trigger. */
  leading?: React.ReactNode;
};

const SIZE_CLASS = { sm: 'max-w-2xl', md: 'max-w-4xl', lg: 'max-w-6xl' } as const;

/** Icon + label, shared between the inline button and the ⋮ menu item. */
function ActionContent({ action }: { action: PageHeaderAction }) {
  const Icon = action.icon;
  return (
    <>
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {action.label}
    </>
  );
}

function ActionButton({
  action,
  Link,
  renderDisabled,
}: {
  action: PageHeaderAction;
  Link?: PageHeaderLinkComponent;
  renderDisabled?: (node: React.ReactNode, reason: string) => React.ReactNode;
}) {
  const className = cn('gap-2', pageHeaderActionClass);
  const disabled = actionIsDisabled(action);
  if (action.to && Link && !disabled) {
    return (
      <Button asChild size="sm" variant={action.variant ?? 'default'} className={className}>
        <Link to={action.to} onClick={action.onClick}>
          <ActionContent action={action} />
        </Link>
      </Button>
    );
  }
  const button = (
    <Button
      size="sm"
      variant={action.variant ?? 'default'}
      className={className}
      disabled={disabled}
      onClick={action.onClick}
    >
      <ActionContent action={action} />
    </Button>
  );
  return action.disabledReason && renderDisabled ? <>{renderDisabled(button, action.disabledReason)}</> : button;
}

export function PageHeader({
  title,
  subtitle,
  subtitleWrap = false,
  badge,
  back,
  actions,
  overflow,
  extra,
  extraCompact = false,
  renderDisabled,
  size,
  moreLabel = 'More actions',
  linkComponent,
  className,
  leading,
}: PageHeaderProps) {
  const { inline, menu } = splitPageHeaderActions(actions);
  const hasMenu = menu.length > 0 || Boolean(overflow);
  const hasActions = inline.length > 0 || hasMenu || Boolean(extra);
  // A header whose only action is the ⋮ keeps it on the title row on phones
  // (top-right) instead of dropping a whole row for one icon button.
  const menuOnly = inline.length === 0 && ((hasMenu && !extra) || (!hasMenu && Boolean(extra) && extraCompact));
  const nonDestructiveMenu = menu.filter((action) => !action.destructive);
  const destructiveMenu = menu.filter((action) => action.destructive);

  const renderMenuAction = (action: PageHeaderAction) => {
    const Link = linkComponent;
    const disabled = actionIsDisabled(action);
    if (action.to && Link && !disabled) {
      return (
        <DropdownMenuItem key={action.key} asChild onSelect={action.onClick}>
          <Link to={action.to}>
            <ActionContent action={action} />
          </Link>
        </DropdownMenuItem>
      );
    }
    if (action.disabledReason) {
      return (
        <DropdownMenuItem
          key={action.key}
          disabled
          className={cn('flex-col items-start gap-0.5', action.destructive && 'text-destructive focus:text-destructive')}
        >
          <span className="flex items-center gap-2">
            <ActionContent action={action} />
          </span>
          <span className="text-xs font-normal text-muted-foreground">{action.disabledReason}</span>
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem
        key={action.key}
        disabled={action.disabled}
        onSelect={action.onClick}
        className={cn(action.destructive && 'text-destructive focus:text-destructive')}
      >
        <ActionContent action={action} />
      </DropdownMenuItem>
    );
  };

  return (
    <header
      className={cn(
        'w-full',
        size && cn(SIZE_CLASS[size], 'mx-auto'),
        back ? 'mb-[25.6px]' : 'mb-[19.2px]',
        className,
      )}
    >
      {back ? (() => {
        const backClassName = 'mb-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-surface-500 transition-colors hover:text-surface-800';
        const BackLink = linkComponent;
        if (back.to && BackLink) {
          return (
            <BackLink to={back.to} onClick={back.onClick} className={backClassName}>
              <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
            </BackLink>
          );
        }
        return (
          <button type="button" onClick={back.onClick} className={backClassName}>
            <ArrowLeft className="h-3.5 w-3.5" /> {back.label}
          </button>
        );
      })() : null}

      <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between', menuOnly && 'max-sm:relative')}>
        <div className={cn('flex min-w-0 items-center gap-3', menuOnly && 'max-sm:pr-12')}>
          {leading}
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
              <h1 className="min-w-0 truncate font-display text-2xl font-semibold tracking-tight text-surface-900 sm:text-[30px]">
                {title}
              </h1>
              {badge ? <span className="shrink-0">{badge}</span> : null}
            </div>
            {subtitle ? (
              <p
                className={cn(
                  'mt-1 text-sm text-muted-foreground',
                  subtitleWrap ? 'text-pretty' : 'truncate',
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {hasActions ? (
          <div className={cn('flex shrink-0 flex-wrap items-center gap-2', menuOnly && 'max-sm:absolute max-sm:right-0 max-sm:top-0')}>
            {inline.map((action) => (
              <ActionButton key={action.key} action={action} Link={linkComponent} renderDisabled={renderDisabled} />
            ))}
            {extra ? <>{extra}</> : null}
            {hasMenu ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-[38.4px] w-[38.4px] shrink-0 sm:h-8 sm:w-8" aria-label={moreLabel}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {overflow}
                  {nonDestructiveMenu.map((action) => renderMenuAction(action))}
                  {destructiveMenu.length > 0 ? <DropdownMenuSeparator /> : null}
                  {destructiveMenu.map((action) => renderMenuAction(action))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
